import React from 'react';
import { motion } from 'framer-motion';
import { Target, Award, Users, TrendingUp } from 'lucide-react';
import { Navbar } from '@/components/Navbar';

import logoImage from '../assets/images/logo.png';

export default function AboutPage() {
  const values = [
    {
      icon: <Target className="w-8 h-8" />,
      title: 'Our Mission',
      description: 'To simplify real estate transactions and provide comprehensive property solutions that bring peace of mind to our clients.'
    },
    {
      icon: <Award className="w-8 h-8" />,
      title: 'Excellence',
      description: 'Committed to delivering the highest quality service with professionalism, integrity, and attention to detail.'
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: 'Customer First',
      description: 'Every decision we make is centered around what\'s best for our clients – your success is our success.'
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: 'Growth & Innovation',
      description: 'Constantly evolving with market trends and technology to provide cutting-edge real estate solutions.'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Header */}
      <section className="relative h-[50vh] flex items-center justify-center overflow-hidden bg-primary">

        {/* Transparent Navbar */}
        <Navbar transparent={true} />

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center text-white">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="font-heading text-5xl md:text-6xl font-medium mb-6 text-shadow-sm">About Sara Realty</h1>
            <p className="text-lg md:text-xl text-white/90 max-w-3xl mx-auto">
              Building Trust, Delivering Excellence
            </p>
          </motion.div>
        </div>
      </section>

      {/* Who We Are Content */}
      <section className="py-12 px-2 md:px-4">
        <div className="max-w-[98%] mx-auto">
          <div className="w-full mx-auto mb-12">
            <div className="bg-[#FFFBF5] p-8 md:p-12 rounded-3xl border-2 border-stone-200 shadow-lg">
              {/* Who We Are Content */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                <motion.div
                  initial={{ opacity: 0, x: -40 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, ease: 'easeOut' }}
                >
                  <h2 className="font-heading text-4xl md:text-5xl font-medium text-primary mb-6">
                    Who We Are
                  </h2>
                  <div className="space-y-4 text-[#57534E] text-lg leading-relaxed">
                    <p>
                      Sara Realty is a premier real estate firm dedicated to simplifying property transactions and providing comprehensive solutions. With a deep commitment to integrity and excellence, we have transformed the way people buy, sell, and manage properties in Tamil Nadu.
                    </p>
                    <p>
                      Our journey is defined by a passion for creating value and building lasting relationships. We prioritize our clients' needs, offering best-in-class service and expert guidance in the dynamic real estate market. Whether you are looking for your dream home or a lucrative investment, Sara Realty is your trusted partner.
                    </p>
                  </div>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, x: 40 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, ease: 'easeOut' }}
                >
                  <div className="relative h-[320px] max-w-md mx-auto rounded-3xl overflow-hidden shadow-2xl transition-transform duration-500 hover:scale-[1.02] bg-white border border-stone-100">
                    <img
                      src={logoImage}
                      alt="Sara Realty"
                      className="w-full h-full object-contain p-12"
                    />
                  </div>
                </motion.div>
              </div>

              {/* Divider */}
              <div className="w-full h-px bg-stone-200 my-12" />

              {/* Founder Section */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, ease: 'easeOut' }}
              >
                <h2 className="font-heading text-3xl md:text-4xl font-medium text-primary mb-2">
                  Mr. Saravanan
                </h2>
                <h3 className="text-xl text-amber-700 mb-6 font-medium">Founder</h3>
                <div className="space-y-4 text-[#57534E] text-lg leading-relaxed text-left">
                  <p>
                    Mr. Saravanan, the visionary founder of Sara Realty, brings over <strong>20+ years of extensive experience</strong> in the real estate industry. His journey is built on a foundation of trust, transparency, and a deep understanding of the market.
                  </p>
                  <p>
                    Known for his unwavering commitment to client satisfaction, Mr. Saravanan has guided countless families and investors to their dream properties. His expertise and ethical approach have earned him a reputation as one of the most trustworthy figures in the sector. Under his leadership, Sara Realty continues to set new benchmarks in quality and reliability.
                  </p>
                </div>
              </motion.div>

              {/* Divider */}
              <div className="w-full h-px bg-stone-200 my-12" />

              {/* Values */}
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, ease: 'easeOut' }}
              >
                <h2 className="font-heading text-4xl md:text-5xl font-medium text-primary mb-12 text-center">
                  Our Values
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                  {values.map((value, index) => (
                    <div
                      key={index}
                      className="p-8 bg-white border border-stone-100 rounded-2xl text-center shadow-sm hover:shadow-md transition-all duration-300"
                    >
                      <div className="w-16 h-16 bg-secondary rounded-2xl flex items-center justify-center text-primary mb-4 mx-auto">
                        {value.icon}
                      </div>
                      <h3 className="font-heading text-xl font-medium text-primary mb-3">
                        {value.title}
                      </h3>
                      <p className="text-[#57534E] leading-relaxed">
                        {value.description}
                      </p>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-24 px-6 md:px-12 lg:px-24 bg-primary text-white">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
          >
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-center">
              {[
                { number: '500+', label: 'Properties Sold' },
                { number: '1000+', label: 'Happy Clients' },
                { number: '20+', label: 'Years Experience' },
                { number: '50+', label: 'Cities Covered' }
              ].map((stat, idx) => (
                <div key={idx}>
                  <div className="font-heading text-5xl md:text-6xl font-medium mb-2">{stat.number}</div>
                  <div className="text-lg text-white/90">{stat.label}</div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}